import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getScopeDetails } from "../../services/scopeService";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import { renderCurrencyIcon, renderCurrencyIconColored } from "../../utils/CurrencyUtils";
import styles from "./styles";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import { GoPackage } from "react-icons/go";
import RfqBoqItemRow from "../RFQPricing/components/RfqBoqItemRow";

const computeBoqSummary = (inclusionsList) => {
  const list = Array.isArray(inclusionsList) ? inclusionsList : [];
  let totalBoqs = 0;
  let coveredBoqs = 0;
  let totalBoqCost = 0;
  for (const inc of list) {
    for (const boq of inc.boqs || []) {
      // Externally-procured BOQs (User/Reno) are not billed by contractor.
      // Exclude from completion count and from total billable cost.
      const externallyProcured =
        boq.procurement === "User" || boq.procurement === "Reno";
      if (externallyProcured) continue;
      totalBoqs += 1;
      const cost = Number(boq.total_cost);
      if (cost !== 0 && !Number.isNaN(cost)) {
        coveredBoqs += 1;
        totalBoqCost += cost;
      }
    }
  }
  return { totalBoqs, coveredBoqs, totalBoqCost };
};

const BOMSummaryDetails = () => {
  const { id, scopeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const bomStyles = styles(isMobile);

  // Get project details and scope ID from location state (passed from navigation)
  const { projectDetails, selectedScopeId, selectedScopeName, passedStyles } =
    location.state || {};
  const details = projectDetails || {};
  const currentScopeId = scopeId || selectedScopeId;

  const [inclusions, setInclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeData, setScopeData] = useState(null);
  const [error, setError] = useState(null);

  const scopeName = selectedScopeName || "Scope";

  const boqSummary = useMemo(() => computeBoqSummary(inclusions), [inclusions]);

  useEffect(() => {
    if (currentScopeId) {
      loadBOMData();
    }
  }, [currentScopeId]);

  const loadBOMData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Loading BOM data for scope ID:", currentScopeId);
      const response = await getScopeDetails(currentScopeId);

      if (response.success) {
        setScopeData(response);

        // Extract all inclusions from all rooms
        const allInclusions =
          response.rooms?.flatMap(
            (room) =>
              room.inclusions?.map((inclusion) => ({
                ...inclusion,
                roomName: room.room_name,
                roomId: room.room_id,
              })) || []
          ) || [];
        setInclusions(allInclusions);
      } else {
        setError(new Error("Failed to load BOM data"));
      }
    } catch (error) {
      console.error("Error loading BOM data:", error);
      setError(error);
      toast.error("Failed to load BOM data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/project/details/${details?.proposal_id}/scopes`);
  };

  const renderSummaryCards = () => {
    if (!boqSummary || boqSummary.totalBoqs === 0) return null;
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          gap: 1.5,
          px: { xs: 2, md: 4 },
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "12px",
            backgroundColor: "#EFF2F580",
            px: 2.5,
            py: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              fontSize: 28,
              fontWeight: 700,
              color: "#4c6bf6",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              "& svg": { width: 24, height: 24 },
            }}
          >
            {renderCurrencyIconColored("large")}
            {formattedAmountWithoutAed(boqSummary.totalBoqCost)}
          </Box>
          <Box sx={{ mt: 1, fontSize: 13, fontWeight: 500, color: "#627084" }}>
            Total BOQ Cost
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "12px",
            backgroundColor: "#E7B0081A",
            px: 2.5,
            py: 3,
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: 22, fontWeight: 700, color: "#E7B008" }}>
            {boqSummary.coveredBoqs} / {boqSummary.totalBoqs}
          </Box>
          <Box sx={{ mt: 1, fontSize: 13, fontWeight: 500, color: "#627084" }}>
            Completion
          </Box>
        </Box>
      </Box>
    );
  };

  const renderItemBreakdown = () => {
    const allInclusions = inclusions || [];

    return (
      <Box sx={bomStyles.itemBreakdownContainer}>
        {allInclusions?.map((inclusion) => (
          <Card key={inclusion.id} sx={bomStyles.inclusionCard}>
            <CardContent sx={bomStyles.inclusionCardContent}>
              <Box sx={bomStyles.inclusionHeader}>
                <Box sx={bomStyles.inclusionTitleContainer}>
                  <GoPackage size={20} />
                  <div style={bomStyles.inclusionTitle}>{inclusion.name}</div>
                </Box>
              </Box>

              <Box sx={bomStyles.boqsContainer}>
                {inclusion?.boqs && inclusion.boqs.length > 0 ? (
                  <Box sx={bomStyles.boqsList}>
                    {inclusion.boqs.map((boq) => (
                      <RfqBoqItemRow
                        key={boq.id}
                        boq={boq}
                        roomName={inclusion.roomName}
                        isDisabled
                      />
                    ))}
                  </Box>
                ) : (
                  <Box sx={bomStyles.noBoqsContainer}>
                    <Typography variant="body2" sx={bomStyles.noBoqsText}>
                      No BOQ items found for this inclusion
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}

        {allInclusions?.length === 0 && (
          <Card sx={bomStyles.emptyStateCard}>
            <CardContent sx={bomStyles.emptyStateContent}>
              <Typography variant="body1" sx={bomStyles.emptyStateText}>
                No inclusions found for this scope
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={bomStyles.loadingContainer}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={bomStyles.loadingContainer}>
        <Typography variant="body1" sx={{ color: "#DC2626" }}>
          Error loading BOM Summary: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!currentScopeId) {
    return (
      <Box sx={bomStyles.container}>
        <Box sx={bomStyles.header}>
          <Box sx={bomStyles.headerLeft}>
            <IconButton onClick={handleBack} sx={bomStyles.backButton}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={bomStyles.title}>
                BOM Summary
              </Typography>
              <Typography variant="body2" sx={bomStyles.subtitle}>
                No scope selected
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Please select a scope to view its BOM summary.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        ...passedStyles.container,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <header style={{ ...passedStyles.header, flexShrink: 0 }}>
        <div style={passedStyles.headerContent}>
          <LeftArrowIcon
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer" }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div
              style={{
                ...passedStyles.idText,
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              {scopeName} - BOM Summary
            </div>
            <div style={{ color: "#949daa", fontSize: "14px" }}>
              Detailed bill of materials breakdown for contracted scope
            </div>
          </div>
        </div>
      </header>

      <Box sx={{ flexShrink: 0 }}>{renderSummaryCards()}</Box>

      <Box
        sx={{
          ...bomStyles.tabContent,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {renderItemBreakdown()}
      </Box>
    </div>
  );
};

export default BOMSummaryDetails;
